import React from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { toast } from 'react-toastify';

import SahamDrawer from 'components/SahamDrawer';
import messages from 'containers/IdentificationPage/messages';
import { translate } from 'containers/services/translate';
import styled from 'styled-components';
import { Select, MenuItem } from '@material-ui/core';
import upload from 'images/upload.svg';
import { Listings, Config } from 'containers/AppContainer/typings';
import { get, isEmpty, isUndefined, isObject, uniqueId } from 'lodash';
import {
  getRequiredDocumentsToIndex,
  existingClaimFormater,
} from 'containers/IdentificationPage/SearchResults/ClaimWithNotIndexedPV/utils';
import { makeSelectDocument } from 'containers/UploadDocuments/selectors';
import { uploadDocument } from 'containers/UploadDocuments/actions';
import { sendExistingClaimAndDocument } from 'containers/IdentificationPage/actions';
import { DEFAULT_DOCUMENT_TYPE } from 'containers/UploadDocuments/constants';
import { DOCUMENT, EMPTY_DOCUMENT } from 'containers/data-structure';
import { DOCUMENT_NAME } from 'containers/IdentificationPage/SearchResults/ClaimWithNotIndexedPV/constants';
import { DOCUMENT_STATUS } from 'containers/ManagerDocumentsPage/constants';
import {
  IndexedDocument,
  Policy,
  SagisClaim,
} from 'containers/IdentificationPage/typings';
import { fileSelectHandler } from 'containers/LawyerLegalProceedingsPage/judgementAdd/utils';
import PrimaryButton from 'components/Buttons/PrimaryButton';

export interface IndexDocumentDrawerProps {
  intl: IntlShape;
  open: boolean;
  handleToggleDrawer: Function;
  listings: Listings;
  claim: SagisClaim;
  policy: Policy;
  uploadDocument: Function;
  sendExistingClaimAndDocument: Function;
  uploadedDocument: { response: string } | {};
}

const DrawerCantiner = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: flex-start;
  background: #fafafa 0% 0% no-repeat padding-box;
  box-shadow: 0px 2px 10px #00000033;
  padding: 10px;

  .button {
    height: 36px;
    background: #f9be00 0% 0% no-repeat padding-box;
    float: left;
    margin-top: 15px;
  }
  .document {
    height: 55px;
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    padding: 10px;
    background: #ffffff 0% 0% no-repeat padding-box;
    box-shadow: 0px 1px 3px #0000001f;
    border-radius: 4px;
  }
  .title-h1 {
    text-align: left;
    font-size: 16px;
    font-family: Roboto;
    font-weight: Medium;
    color: #2b2727;
  }
  .title {
    text-align: left;
    font-size: 13px;
    font-family: Roboto;
    font-weight: Medium;
    letter-spacing: 0px;
    color: #2b2727;
  }
  .type {
    text-align: left;
    font-size: 13px;
    font-family: Roboto;
    font-weight: Regular;
    letter-spacing: 0px;
    color: #999999;
    text-transform: uppercase;
  }
  .margin-TB {
    margin: 15px 0 15px 0;
  }
  .margin-B {
    margin: 0 0 15px 0;
  }
`;

const IndexDocumentDrawer: React.FC<IndexDocumentDrawerProps> = (
  props,
): JSX.Element => {
  const fileRef: React.RefObject<HTMLInputElement> = React.createRef();

  /* istanbul ignore next */
  const [documentType, setDocumentType] = React.useState(DEFAULT_DOCUMENT_TYPE);
  /* istanbul ignore next */
  const [uploadedDocs, setUploadedDocs] = React.useState<any[]>([]);

  /* istanbul ignore next */
  const handleChange = (
    event: React.ChangeEvent<{
      name?: string;
      value?: any;
    }>,
  ) => {
    const { value } = event.target;
    setDocumentType(value);
  };

  /* istanbul ignore next */
  React.useEffect(() => {
    if (props.claim?.documentsIndexed)
      setUploadedDocs([...props.claim.documentsIndexed]);
  }, [props.claim]);

  /* istanbul ignore next */
  const uploadClick = () => {
    fileRef.current?.click();
  };

  /* istanbul ignore next */
  React.useEffect(() => {
    const documents = [...uploadedDocs];
    const newDocument: any = {
      ...EMPTY_DOCUMENT,
      [DOCUMENT.IS_INDEXED]: false,
      [DOCUMENT.IS_VISIBLE]: true,
      [DOCUMENT.NAME]: `Document ${uploadedDocs.length}`,
      [DOCUMENT.TYPE]: documentType,
      [DOCUMENT.URL]: get(props.uploadedDocument, 'response'),
    };

    const isPresent = uploadedDocs.filter(
      (doc: { url: string }) => doc.url === newDocument[DOCUMENT.URL],
    );

    if (isPresent.length === 0 && !isEmpty(props.uploadedDocument)) {
      documents.push(newDocument as never);
    }

    setUploadedDocs(documents);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.uploadedDocument]);

  /* istanbul ignore next */
  const openClaimSuccessCallback = () => {
    props.handleToggleDrawer();
    toast.success(translate(messages.validationSuccessMessage, props));
  };

  /* istanbul ignore next */
  const handleValidate = () => {
    const _claim = existingClaimFormater(props.claim, props.policy);
    const _documents = uploadedDocs.filter(
      (ele: { id: number }) => isObject(ele) && isUndefined(ele.id),
    );
    const payload = {
      claim: _claim,
      documents: _documents.map(e => ({
        ...e,
        claimId: null,
        status: DOCUMENT_STATUS.TO_VALIDATE,
      })),
    };
    props.sendExistingClaimAndDocument(payload, openClaimSuccessCallback);
  };

  return (
    <SahamDrawer
      open={props.open}
      handleToggleDrawer={props.handleToggleDrawer}
      handleValidateDrawer={handleValidate}
      title={translate({ ...messages.indexDocumentDrawerTitle }, props)}
      confirmButtonTitle={translate(
        messages.saveIndexedDocumentButtonTitle,
        props,
      )}
    >
      <DrawerCantiner>
        <Select
          onChange={handleChange}
          className="margin-TB"
          name="documentType"
          value={documentType}
          labelWidth={60}
        >
          {!isEmpty(get(props.listings, 'requiredDocument')) &&
            !isEmpty(props.claim) &&
            getRequiredDocumentsToIndex(
              get(props.listings, 'requiredDocument'),
              props.claim?.documents as IndexedDocument[],
            ).map((element: Config) => (
              <MenuItem
                value={element.name}
                key={uniqueId(`required-document-${element.id}`)}
              >
                {element.name}
              </MenuItem>
            ))}
        </Select>
        <input
          id="selectImage"
          type="file"
          onChange={fileSelectHandler(props.uploadDocument, props)}
          ref={fileRef}
          hidden
        />

        <PrimaryButton
          className="button margin-TB"
          onClick={uploadClick}
          lefticon={<img src={upload} alt="null" />}
          title={translate({ ...messages.indexDocumentDrawerDownload }, props)}
        />
        <span className="title-h1 margin-TB">Les documents importés</span>
        {/* istanbul ignore next */
        !isEmpty(uploadedDocs) &&
          uploadedDocs.map(elem => (
            <div key={uniqueId('document-')} className="document margin-B">
              <div className="title">
                <span>{elem.name || DOCUMENT_NAME}</span>
              </div>
              <div className="type">
                <span>{elem.type || elem}</span>
              </div>
            </div>
          ))}
      </DrawerCantiner>
    </SahamDrawer>
  );
};

const mapDispatchToProps = {
  uploadDocument,
  sendExistingClaimAndDocument,
};
const mapStateToProps = createStructuredSelector({
  uploadedDocument: makeSelectDocument(),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(IndexDocumentDrawer));
